import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { Loading } from "@/components/shared/Loading";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/api/client";
import { apiErrorMessage, isConflict } from "@/lib/apiError";
import { useOperationsHandoff, useJob } from "@/api/hooks";
import { HandoffConfirmDialog } from "@/components/shared/HandoffConfirmDialog";
import { money } from "@/lib/format";
import { ArrowLeft, Check, RotateCcw, AlertTriangle, RefreshCw, Loader2, Package, MapPin, Plane } from "lucide-react";

const RETURN_REASONS = [
  { code: "missing_shipper_contact", label: "Missing shipper contact" },
  { code: "missing_delivery_address", label: "Missing consignee address" },
  { code: "missing_cargo_data", label: "Cargo data unclear" },
  { code: "unclear_schedule", label: "Schedule missing" },
  { code: "required_document_missing", label: "Required document missing" },
  { code: "ambiguous_commercial_scope", label: "Scope ambiguous" },
  { code: "inconsistent_finance_release", label: "Finance-release inconsistency" },
];

// Phase 4.5 Part 16/17/18 — Operations Handoff review. Deliberately kept
// simple: this phase only gets the order safely into Operations' queue.
// No Booking/execution action exists here.
export default function OpsHandoffReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [returnOpen, setReturnOpen] = useState(false);
  const [acceptConfirmOpen, setAcceptConfirmOpen] = useState(false);
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: h, isLoading } = useOperationsHandoff(id);
  const { data: job } = useJob(h?.jobId);
  if (isLoading || !h) return <Loading />;

  // Real staleness check (Part 18): compare the version the handoff was
  // generated against to the job's CURRENT version from a fresh fetch —
  // never a client-side guess.
  const stale = job && h.jobVersionAtCreation != null && job.version != null && job.version !== h.jobVersionAtCreation;
  const decided = h.status !== "PENDING_OPERATIONS_REVIEW" && h.status !== "RETURNED_FOR_CLARIFICATION";

  const refresh = () => { qc.invalidateQueries({ queryKey: ["op-handoff", id] }); qc.invalidateQueries({ queryKey: ["job"] }); };

  const accept = async () => {
    setBusy(true);
    try {
      setAcceptConfirmOpen(false);
      // Freshness rule (Part 24): re-fetch first, then act — never trust a
      // screen that may be minutes old.
      await qc.invalidateQueries({ queryKey: ["op-handoff", id] });
      await qc.invalidateQueries({ queryKey: ["job", h.jobId] });
      await api.post(`/operations-handoffs/${id}/accept`, { expectedVersion: h.version });
      toast.success("Handoff Accepted — Ready for Operations Planning");
      refresh();
    } catch (e) {
      if (isConflict(e)) {
        toast.error("The Job handoff changed while you were reviewing it. Refresh before deciding.");
      } else {
        toast.error(apiErrorMessage(e, "Could not accept handoff"));
      }
      refresh();
    } finally { setBusy(false); }
  };

  const submitReturn = async () => {
    if (!reason) { toast.error("Choose a reason"); return; }
    setBusy(true);
    try {
      await api.post(`/operations-handoffs/${id}/reject`, { expectedVersion: h.version, reasonCode: reason, note });
      toast.success("Returned for clarification — the responsible team has been notified");
      setReturnOpen(false); setReason(null); setNote("");
      refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not return handoff"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        title={<span className="flex items-center gap-2"><button onClick={() => navigate("/operations/inbox")} className="text-slate-400 hover:text-slate-700"><ArrowLeft className="h-4 w-4" /></button>{h.jobId}</span>}
        subtitle={`${job?.customer || h.clientId} · ${h.mode} ${h.direction}`}
        actions={<StatusPill status={h.status} />} />

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {stale && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="handoff-stale-banner">
            <RefreshCw className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-amber-800 text-sm">This handoff has changed. Refresh before deciding.</div>
              <div className="text-[12px] text-amber-700 mt-0.5">The Job was updated since this handoff was generated — accepting now would use stale information.</div>
            </div>
            <Button size="sm" variant="outline" className="h-8" onClick={refresh} data-testid="handoff-refresh">Refresh</Button>
          </div>
        )}

        {h.status === "RETURNED_FOR_CLARIFICATION" && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Previously returned</div>
            <div className="text-sm text-slate-700">Reason: {RETURN_REASONS.find((r) => r.code === h.rejectionReason)?.label || h.rejectionReason}</div>
            {h.rejectionNote && <div className="text-[13px] text-slate-500 mt-1">"{h.rejectionNote}"</div>}
            <div className="text-[12px] text-slate-400 mt-1">Waiting on the responsible team to correct and resubmit.</div>
          </div>
        )}

        {h.status === "ACCEPTED" && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-2" data-testid="handoff-accepted-banner">
            <Check className="h-5 w-5 text-emerald-600" />
            <div className="font-semibold text-emerald-800">Handoff Accepted — Ready for Operations Planning</div>
          </div>
        )}

        <Card title="Route & scope">
          <Grid>
            <Stat label="Mode / Direction" value={`${h.mode} · ${h.direction}`} icon={Plane} />
            <Stat label="Route" value={`${h.origin || "—"} → ${h.destination || "—"}`} icon={MapPin} />
            <Stat label="Scope" value={h.scope} />
            <Stat label="Incoterm" value={h.incoterm || "—"} />
            <Stat label="Equipment" value={h.equipment || "—"} />
            <Stat label="Pickup / Delivery" value={`${h.pickup || "—"} / ${h.delivery || "—"}`} />
          </Grid>
        </Card>

        <Card title="Cargo">
          <Grid>
            <Stat label="Commodity" value={h.cargo?.commodity || "—"} icon={Package} />
            <Stat label="Pieces" value={h.cargo?.pieces ?? "—"} />
            <Stat label="Weight" value={h.cargo?.weight ?? "—"} />
            <Stat label="CBM" value={h.cargo?.cbm ?? "—"} />
          </Grid>
          {(h.specialHandling?.dg || h.specialHandling?.battery || h.specialHandling?.temperatureControlled || h.specialHandling?.restricted) && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {h.specialHandling?.dg && <StatusPill status="Dangerous Goods" bucket="danger" />}
              {h.specialHandling?.battery && <StatusPill status="Battery" bucket="warning" />}
              {h.specialHandling?.temperatureControlled && <StatusPill status="Temp Controlled" bucket="info" />}
              {h.specialHandling?.restricted && <StatusPill status="Restricted" bucket="danger" />}
            </div>
          )}
        </Card>

        <Card title="Schedule & parties">
          <Grid>
            <Stat label="Cargo ready" value={h.schedule?.cargoReadyDate || "—"} />
            <Stat label="Requested delivery" value={h.schedule?.requestedDeliveryDate || "—"} />
            <Stat label="Shipper" value={h.shipper || "—"} />
            <Stat label="Consignee" value={h.consignee || "—"} />
            <Stat label="Sales owner" value={h.salesOwner || "—"} />
          </Grid>
        </Card>

        <Card title="Documents">
          <div className="text-[12px] text-slate-500 mb-2">Received: {(h.documentsReceived || []).length ? h.documentsReceived.join(", ") : "None on file yet"}</div>
          {(h.operationalDocumentsMissing || []).length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="handoff-missing-docs">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>Known missing operational information: {h.operationalDocumentsMissing.join(", ")}</div>
            </div>
          )}
        </Card>

        <Card title="Finance">
          <Grid>
            <Stat label="Accepted quotation" value={`${h.acceptedQuotationRef?.quotationId || "—"} V${h.acceptedQuotationRef?.version ?? ""}`} />
            <Stat label="Finance status" value={h.financeReleaseSummary?.decision || "—"} accent={h.financeReleaseSummary?.decision === "RELEASED"} />
          </Grid>
          <div className="text-[11px] text-slate-400 mt-2">Finance's internal exposure/credit rationale is not shown here — this is an Operations screen.</div>
        </Card>

        {!decided && (
          <div className="flex items-center gap-2 pt-1">
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setAcceptConfirmOpen(true)} disabled={busy || stale} data-testid="handoff-accept">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}Accept Handoff
            </Button>
            <Button variant="outline" onClick={() => setReturnOpen(true)} disabled={busy} data-testid="handoff-return">
              <RotateCcw className="h-4 w-4 mr-1.5" />Return for Clarification
            </Button>
          </div>
        )}
      </div>

      <HandoffConfirmDialog
        open={acceptConfirmOpen}
        onOpenChange={setAcceptConfirmOpen}
        busy={busy}
        title="Accept Finance -> Operations handoff"
        confirmLabel="Accept handoff"
        summaryRows={[
          { label: "Job", value: h.jobId },
          { label: "Customer", value: job?.customer || h.clientId },
          { label: "Mode / Direction", value: `${h.mode} · ${h.direction}` },
          { label: "Accepted quotation", value: `${h.acceptedQuotationRef?.quotationId || "—"} V${h.acceptedQuotationRef?.version ?? ""}` },
          { label: "Accepted amount", value: h.acceptedQuotationRef?.total != null ? money(h.acceptedQuotationRef.total, h.acceptedQuotationRef.currency) : "—" },
          { label: "Finance status", value: h.financeReleaseSummary?.decision || "—" },
        ]}
        receivingTeamSees={[
          "This Job moves into your Operations queue as Ready for Operations Planning.",
          "Booking, cutoffs, document collection and execution become your responsibility from this point.",
        ]}
        onConfirm={accept}
      />

      <Dialog open={returnOpen} onOpenChange={setReturnOpen}>
        <DialogContent className="sm:max-w-md" data-testid="handoff-return-dialog">
          <DialogHeader><DialogTitle className="font-display">Return for clarification</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            {RETURN_REASONS.map((r) => (
              <button key={r.code} onClick={() => setReason(r.code)} data-testid={`return-reason-${r.code}`}
                className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors ${reason === r.code ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                {r.label}
              </button>
            ))}
          </div>
          <Textarea rows={3} placeholder="Optional note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="return-note" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReturnOpen(false)}>Cancel</Button>
            <Button onClick={submitReturn} disabled={busy || !reason} className="bg-amber-600 hover:bg-amber-700" data-testid="handoff-return-submit">
              {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Card = ({ title, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">{title}</div>
    {children}
  </div>
);
const Grid = ({ children }) => <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{children}</div>;
const Stat = ({ label, value, accent }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
    <div className={`text-sm font-semibold mt-0.5 ${accent ? "text-emerald-600" : "text-slate-800"}`}>{value}</div>
  </div>
);
